/**
 * Villy Shop core (plain script, no dependencies).
 * window.VillyShop: dialog helpers, scroll lock, live region, countdown, accordion animation, contrast switch.
 * Custom elements: <vs-carousel>, <vs-countdown>.
 * Every element tolerates connectedCallback running twice (theme editor) and cleans up in disconnectedCallback.
 */
(function () {
  'use strict';
  if (window.VillyShop && window.VillyShop.__core) return;

  var doc = document;
  var root = doc.documentElement;
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function store(kind, key, value) {
    try {
      var s = kind === 'session' ? window.sessionStorage : window.localStorage;
      if (value === undefined) return s.getItem(key);
      if (value === null) s.removeItem(key); else s.setItem(key, value);
    } catch (e) { /* storage can be blocked */ }
    return null;
  }

  /* ---------- scroll lock (counted, so two dialogs can overlap) ---------- */
  var locks = 0;
  function lockScroll() {
    locks += 1;
    if (locks === 1) root.classList.add('vs-no-scroll');
  }
  function unlockScroll() {
    locks = Math.max(0, locks - 1);
    if (locks === 0) root.classList.remove('vs-no-scroll');
  }

  /* ---------- live region ---------- */
  function live(message) {
    var el = doc.querySelector('.vs-live');
    if (!el) return;
    el.textContent = '';
    window.setTimeout(function () { el.textContent = message; }, 40);
  }

  /* ---------- dialogs: open with showModal, close after the 150 ms fade ---------- */
  function openDialog(dlg, options) {
    options = options || {};
    if (!dlg || dlg.open) return;
    if (options.modeless && typeof dlg.show === 'function') dlg.show();
    else dlg.showModal();
    if (!options.modeless) lockScroll();
    dlg.__vsLocked = !options.modeless;
    dlg.classList.remove('is-closing');
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { dlg.classList.add('is-open'); });
    });
    dlg.dispatchEvent(new CustomEvent('vs:open'));
  }
  function closeDialog(dlg, immediate) {
    if (!dlg || !dlg.open) return;
    if (dlg.__vsClosing) return;
    dlg.__vsClosing = true;
    function finish() {
      dlg.__vsClosing = false;
      if (dlg.open) dlg.close();
      dlg.classList.remove('is-open', 'is-closing');
      if (dlg.__vsLocked) { dlg.__vsLocked = false; unlockScroll(); }
      dlg.dispatchEvent(new CustomEvent('vs:close'));
    }
    if (immediate || reduceMotion.matches) { finish(); return; }
    dlg.classList.add('is-closing');
    dlg.classList.remove('is-open');
    window.setTimeout(finish, 160);
  }
  /* ESC on a native dialog: run our own fade instead of the instant close */
  doc.addEventListener('cancel', function (event) {
    var dlg = event.target;
    if (!dlg || !dlg.classList || !dlg.classList.contains('vs-dlg')) return;
    event.preventDefault();
    closeDialog(dlg);
  }, true);
  /* a native close that bypassed us (form method=dialog) still has to release the scroll lock */
  doc.addEventListener('close', function (event) {
    var dlg = event.target;
    if (dlg && dlg.__vsLocked) { dlg.__vsLocked = false; unlockScroll(); dlg.classList.remove('is-open', 'is-closing'); }
  }, true);
  function anyDialogOpen() {
    return !!doc.querySelector('dialog.vs-dlg[open]:not([data-vs-modeless]), cart-drawer.active');
  }

  /* ---------- countdown: "5d 5h 47m 16s", UK local time ---------- */
  function ukOffsetMinutes(utcMs) {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
      }).formatToParts(new Date(utcMs));
      var o = {};
      parts.forEach(function (p) { o[p.type] = parseInt(p.value, 10); });
      var asUtc = Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute, o.second);
      return Math.round((asUtc - Math.floor(utcMs / 1000) * 1000) / 60000);
    } catch (e) { return 0; }
  }
  /** "YYYY-MM-DD HH:MM" read as UK local time -> epoch ms (NaN when the text is not a date) */
  function parseUk(text) {
    var m = /^\s*(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(text || '');
    if (!m) return NaN;
    var wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), 0);
    var guess = wall - ukOffsetMinutes(wall) * 60000;
    return wall - ukOffsetMinutes(guess) * 60000;
  }
  function formatLeft(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    var out = [];
    if (d > 0) out.push(d + 'd');
    if (d > 0 || h > 0) out.push(h + 'h');
    out.push(m + 'm');
    out.push(s + 's');
    return out.join(' ');
  }

  class VsCountdown extends HTMLElement {
    connectedCallback() {
      this.end = parseUk(this.getAttribute('end'));
      if (isNaN(this.end)) { this.remove(); return; }
      this.style.fontVariantNumeric = 'tabular-nums';
      this.tick = this.tick.bind(this);
      this.tick();
      window.clearInterval(this.timer);
      this.timer = window.setInterval(this.tick, 1000);
    }
    disconnectedCallback() { window.clearInterval(this.timer); }
    tick() {
      var left = this.end - Date.now();
      if (left <= 0) {
        window.clearInterval(this.timer);
        this.dispatchEvent(new CustomEvent('vs:countdown-end', { bubbles: true }));
        var holder = this.closest('[data-vs-countdown-holder]');
        if (holder) holder.hidden = true;
        this.remove();
        return;
      }
      this.textContent = formatLeft(left);
    }
  }
  if (!customElements.get('vs-countdown')) customElements.define('vs-countdown', VsCountdown);

  /* ---------- carousel ---------- */
  class VsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.vs-car__track');
      if (!this.track || this.__ready) return;
      this.__ready = true;
      this.prev = this.querySelector('.vs-car__prev');
      this.next = this.querySelector('.vs-car__next');
      this.count = this.querySelector('.vs-car__count');
      this.pager = this.querySelector('.vs-car__pager');
      this.onScroll = this.update.bind(this);
      this.onKey = this.key.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKey);
      if (this.prev) this.prev.addEventListener('click', this.step.bind(this, -1));
      if (this.next) this.next.addEventListener('click', this.step.bind(this, 1));
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(this.onScroll);
        this.ro.observe(this.track);
      }
      if (this.getAttribute('data-drag') === 'true') this.enableDrag();
      this.update();
    }
    disconnectedCallback() {
      this.__ready = false;
      if (this.ro) this.ro.disconnect();
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('keydown', this.onKey);
      }
    }
    slides() { return Array.prototype.slice.call(this.track.querySelectorAll(':scope > .vs-car__slide')); }
    stepSize() {
      var s = this.slides();
      if (!s.length) return this.track.clientWidth;
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return s[0].offsetWidth + gap;
    }
    padLeft() { return parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0; }
    index() {
      var s = this.slides();
      var left = this.track.scrollLeft + this.padLeft();
      for (var i = 0; i < s.length; i++) {
        if (s[i].offsetLeft >= left - 4) return i;
      }
      return Math.max(0, s.length - 1);
    }
    update() {
      var t = this.track;
      var s = this.slides();
      var fits = t.scrollWidth <= t.clientWidth + 2;
      if (this.pager) this.pager.hidden = fits || s.length < 2;
      var i = this.index();
      if (this.count) this.count.textContent = (i + 1) + '/' + s.length;
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft >= t.scrollWidth - t.clientWidth - 2;
      this.setAttribute('data-index', String(i));
    }
    step(dir) {
      this.track.scrollBy({ left: dir * this.stepSize(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    goTo(i) {
      var s = this.slides();
      if (!s[i]) return;
      this.track.scrollTo({ left: s[i].offsetLeft - this.padLeft(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    key(event) {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
    }
    enableDrag() {
      var t = this.track; var self = this;
      var down = false; var moved = false; var sx = 0; var sl = 0;
      t.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; sx = e.clientX; sl = t.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - sx;
        if (!moved && Math.abs(dx) > 5) { moved = true; t.classList.add('is-dragging'); }
        if (moved) t.scrollLeft = sl - dx;
      });
      function end() {
        if (!down) return;
        down = false;
        if (!moved) return;
        t.classList.remove('is-dragging');
        var s = self.slides(); var left = t.scrollLeft + self.padLeft(); var best = 0; var bd = Infinity;
        for (var i = 0; i < s.length; i++) {
          var d = Math.abs(s[i].offsetLeft - left);
          if (d < bd) { bd = d; best = i; }
        }
        self.goTo(best);
        window.setTimeout(function () { moved = false; }, 0);
      }
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      t.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
      t.addEventListener('dragstart', function (e) { e.preventDefault(); });
    }
  }
  if (!customElements.get('vs-carousel')) customElements.define('vs-carousel', VsCarousel);

  /* the theme editor: selecting a block that sits in a carousel brings its slide into view */
  doc.addEventListener('shopify:block:select', function (event) {
    var slide = event.target && event.target.closest ? event.target.closest('.vs-car__slide') : null;
    if (slide && slide.parentElement && slide.parentElement.parentElement && slide.parentElement.parentElement.goTo) {
      var car = slide.parentElement.parentElement;
      car.goTo(car.slides().indexOf(slide));
    }
  });

  /* ---------- accordion: <details class="vs-acc"> opens and closes with a 450 ms height animation ---------- */
  function initDetails(scope) {
    var list = (scope || doc).querySelectorAll('details.vs-acc:not([data-vs-ready])');
    Array.prototype.forEach.call(list, function (details) {
      details.setAttribute('data-vs-ready', '1');
      var summary = details.querySelector('summary');
      var body = details.querySelector('.vs-acc__body');
      if (!summary || !body) return;
      summary.addEventListener('click', function (event) {
        if (reduceMotion.matches || !body.animate) return;
        event.preventDefault();
        if (details.__vsBusy) return;
        details.__vsBusy = true;
        var opening = !details.open;
        var done = function () { details.__vsBusy = false; details.style.overflow = ''; };
        details.style.overflow = 'hidden';
        if (opening) {
          details.open = true;
          var h = body.offsetHeight;
          var a = body.animate({ height: ['0px', h + 'px'], opacity: [0, 1] }, { duration: 450, easing: 'cubic-bezier(.31,0,.13,1)' });
          a.onfinish = done; a.oncancel = done;
          summary.setAttribute('aria-expanded', 'true');
        } else {
          var h2 = body.offsetHeight;
          var b = body.animate({ height: [h2 + 'px', '0px'], opacity: [1, 0] }, { duration: 450, easing: 'cubic-bezier(.31,0,.13,1)' });
          b.onfinish = function () { details.open = false; done(); };
          b.oncancel = done;
          summary.setAttribute('aria-expanded', 'false');
        }
      });
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      details.addEventListener('toggle', function () { summary.setAttribute('aria-expanded', details.open ? 'true' : 'false'); });
    });
  }

  /* ---------- disclosure buttons (footer lists): aria-expanded + grid-rows height ---------- */
  function initDisclosures(scope) {
    var list = (scope || doc).querySelectorAll('[data-vs-disclosure]:not([data-vs-ready])');
    Array.prototype.forEach.call(list, function (btn) {
      btn.setAttribute('data-vs-ready', '1');
      var target = doc.getElementById(btn.getAttribute('aria-controls'));
      if (!target) return;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        target.classList.toggle('is-open', open);
        if (open) target.removeAttribute('inert'); else target.setAttribute('inert', '');
      });
    });
  }

  /* ---------- contrast switch (footer) ---------- */
  function setContrast(on) {
    if (on) root.setAttribute('data-vs-contrast', 'high'); else root.removeAttribute('data-vs-contrast');
    store('local', 'vs-contrast', on ? 'high' : 'normal');
    Array.prototype.forEach.call(doc.querySelectorAll('[data-vs-contrast-switch]'), function (sw) {
      sw.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }
  function initContrast() {
    var on = root.getAttribute('data-vs-contrast') === 'high';
    Array.prototype.forEach.call(doc.querySelectorAll('[data-vs-contrast-switch]:not([data-vs-ready])'), function (sw) {
      sw.setAttribute('data-vs-ready', '1');
      sw.setAttribute('aria-checked', on ? 'true' : 'false');
      sw.addEventListener('click', function () { setContrast(sw.getAttribute('aria-checked') !== 'true'); });
    });
  }

  /* ---------- cart drawer: keep .is-empty in step with the basket ----------
     The quick-add chip adds through VillyTheme.addLines, which repaints the drawer but used to leave the empty state on (no header, no
     lines, no way to close it on a phone). Dawn's own product form clears it; this makes every path safe, in both directions. */
  function initCartDrawer() {
    var cd = doc.querySelector('cart-drawer');
    var host = cd && cd.querySelector('#CartDrawer');
    if (!host || host.__vsSync || !window.MutationObserver) return;
    host.__vsSync = true;
    new MutationObserver(function () {
      cd.classList.toggle('is-empty', !cd.querySelector('.cart-item'));
    }).observe(host, { childList: true, subtree: true });
  }

  function init(scope) {
    initDetails(scope);
    initDisclosures(scope);
    initContrast();
    initCartDrawer();
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  doc.addEventListener('shopify:section:load', function (event) { init(event.target); });

  window.VillyShop = {
    __core: true,
    reduceMotion: reduceMotion,
    store: store,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    live: live,
    openDialog: openDialog,
    closeDialog: closeDialog,
    anyDialogOpen: anyDialogOpen,
    parseUk: parseUk,
    formatLeft: formatLeft,
    initDetails: initDetails,
    initDisclosures: initDisclosures,
  };
})();
